import { Fragment, useCallback } from 'react'
import ModalDeleteProductInner from './Subcomponents/ModalDeleteProductInner'
import { useNavigate, useLocation } from 'react-router'
import ModalEditActiveProductInner from './Subcomponents/ModalEditActiveProductInner'
import { ButtonRedOrLoader, ButtonOrLoader, ButtonSecondaryOrLoader } from '../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../baseComponents/CreateRoutering/types'
import { SmallTitleAndRightLayout } from '../../../baseComponents/Layout/TitleAndRightLayout'
import PermissionChecker from '../../../baseComponents/PermissionChecker'
import { getBasePath } from '../../../utils/globals/link'
import useEditDeleteModal from '../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { ProductExtendedTyShort } from '../types'
import ModalPrintProductTags from './Subcomponents/ModalPrintProductTags'
import './product.sass'
import { ModalCreateProductOutside } from '../../../app/erp/screens/Logistic/ProductsParent/Products/ModalCreateProduct'
import { ConnectedProps, connect } from 'react-redux'
import withLoading from '../../../utils/hoc/withLoading'
import { AllReduxPayloads } from '../../../utils/reducers'
import { PdfTemplatePayload } from '../../../utils/reducers/company/pdfTemplates'
import ModalPrintProductCertificateConnect from './Subcomponents/ModalPrintProductCertificate'

type ProductLayoutProps = ComponentWithPermissions & {
    basicData: ProductExtendedTyShort
    buttonIsDisabled: boolean
    children: React.ReactNode
    extraButtons?: React.ReactNode
    extraButtonsReadPermission?: React.ReactNode
}

const ProductLayout: React.FC<ProductLayoutProps> = ({ basicData, buttonIsDisabled, userPermissions, children, extraButtons, extraButtonsReadPermission }) => {
    const { t } = useTwinTranslation()
    
    return (
        <SmallTitleAndRightLayout className='product_layout' title={(basicData.name + ' ' + (basicData?.reference ? ('- ' + basicData?.reference) : '')) || ''} RightHeader={
        <div className='flex gap-10 justify-end items-center'>
            <PermissionChecker userPermissions={userPermissions} permission='read'>
                {extraButtonsReadPermission}
            </PermissionChecker>
            <PermissionChecker userPermissions={userPermissions} permission='update'>
                {extraButtons}
                <ButtonOrLoader textButton={t('save', 'Guardar')} buttonIsDisabled={buttonIsDisabled} />
            </PermissionChecker>
        </div>
    }>
            {children}
        </SmallTitleAndRightLayout>
    )
}



type ProductLayoutWithDeleteProps = ProductLayoutWithDeleteLogicProps & ProductLayoutProps & ReduxOrderSelectorPdfTemplate & {
    getData: () => Promise<void>
}

const ProductLayoutWithDeleteBase: React.FC<ProductLayoutWithDeleteProps> = ({buttonIsDisabled, basicData, locationPath, getData, pdfTemplates, ...rest}) => {
    const { openModal, setOpenModal, onHandleSubmitDelete } = useProductLayoutWithDeleteLogic({ locationPath })
    const {t} = useTwinTranslation()
    let myButtons: JSX.Element[] = []
    let myButtonsReadPermissions: JSX.Element[] = []
    if (Object.keys(pdfTemplates?.productCertificate || {}).length){
        myButtonsReadPermissions.push(<ButtonSecondaryOrLoader key='printCert' textButton={t('printCertificate', 'Imprimir certificado')} buttonIsDisabled={buttonIsDisabled} onClick={(e) => { e.preventDefault(); setOpenModal({ type: 'create', allRowData: { type: 'printCert' } }) }} />)
    }
    myButtonsReadPermissions.push(<ButtonSecondaryOrLoader key='print' textButton={t('printTags', 'Imprimir etiquetas')} buttonIsDisabled={buttonIsDisabled} onClick={(e) => { e.preventDefault(); setOpenModal({ type: 'create', allRowData: { type: 'print' } }) }} />)
    myButtons.push(<ButtonSecondaryOrLoader key='duplicate' textButton={t('duplicateProduct', 'Duplicar producto')} buttonIsDisabled={buttonIsDisabled} onClick={(e) => { e.preventDefault(); setOpenModal({ type: 'create', allRowData: { type: 'duplicate' } }) }} />)
    if (basicData.active) {
        myButtons.push(<ButtonRedOrLoader key='delete' textButton={t('delete', 'Eliminar')} buttonIsDisabled={buttonIsDisabled} onClick={(e) => { e.preventDefault(); setOpenModal({ type: 'delete', allRowData: basicData }) }} />)
    } else if (!basicData.active) {
        myButtons.push(<ButtonSecondaryOrLoader key='active' textButton={t('activeAction', 'Activar')} buttonIsDisabled={buttonIsDisabled} onClick={(e) => { e.preventDefault(); setOpenModal({ type: 'edit', allRowData: basicData }) }} />)
    }
    return (
        <ProductLayout {...{ ...rest, basicData, buttonIsDisabled }} extraButtons={(
            <Fragment>
                { myButtons }
                {openModal?.type === 'delete' ? <ModalDeleteProductInner setOpened={() => setOpenModal(null)} onSubmit={onHandleSubmitDelete} allRowData={basicData} /> : null}
                {openModal?.type === 'edit' ? <ModalEditActiveProductInner setOpened={() => setOpenModal(null)} onSubmit={getData} allRowData={basicData} /> : null}
        </Fragment>
        )}
            extraButtonsReadPermission={(
                <Fragment>
                    {myButtonsReadPermissions}
                    {openModal?.type === 'create' && openModal?.allRowData?.type === 'print' ? <ModalPrintProductTags setOpened={() => setOpenModal(null)} onSubmit={() => { }} basicData={basicData} /> : null}
                    {openModal?.type === 'create' && openModal?.allRowData?.type === 'printCert' ? <ModalPrintProductCertificateConnect setOpened={() => setOpenModal(null)} onSubmit={() => { }} basicData={basicData} /> : null}
                    {openModal?.type === 'create' && openModal?.allRowData?.type === 'duplicate' ? <ModalCreateProductOutside setOpened={() => setOpenModal(null)} onSubmit={() => { }} allRowData={basicData} userPermissions={rest.userPermissions} /> : null}
                </Fragment>
            )} />
    )
}

interface ProductLayoutWithDeleteLogicProps {
    locationPath: string
}

const useProductLayoutWithDeleteLogic = ({ locationPath }: ProductLayoutWithDeleteLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, locationPath || '/')
    const onHandleSubmitDelete = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])

    return { openModal, setOpenModal, onHandleSubmitDelete }
}

const pdfTemplateDispatch = {
    setPdfTemplates: (payload: PdfTemplatePayload) => ({ type: 'CHANGE_PDFTEMPLATE', payload }),
}

export type ReduxOrderSelectorPdfTemplate = ConnectedProps<typeof productLayoutDeleteTemplateConnect>
const mapproductLayoutDeleteTemplateConnector = (state: AllReduxPayloads) => ({ pdfTemplates: state.pdfTemplates })
const productLayoutDeleteTemplateConnect = connect(mapproductLayoutDeleteTemplateConnector, pdfTemplateDispatch)
const productLayoutDeleteTemplateConnectLoading = withLoading(ProductLayoutWithDeleteBase, [{ fetchUrl: '/api/app/config/pdfTemplate/getAllPdfTemplates', propName: 'pdfTemplates', setFunctionName: 'setPdfTemplates' }])
export const ProductLayoutWithDelete = productLayoutDeleteTemplateConnect(productLayoutDeleteTemplateConnectLoading)

export default ProductLayout